import * as React from 'react';
import { Form, Modal, ModalBody, ModalFooter, CustomDatePicker, NumberInput, CustomButton, ButtonType, CustomSubmitButton, ModalSize, HiddenInput, Select, AlertMessage, AlertType } from "soprasteria-ui-library";
import { IEpiReport } from '../../../models/IEpiReport';
import { addToMonth, dateTimeToDateString, formatDate } from '../../../helpers/DateHelper';
import { NINETEEN_TO_TWENTYTHREE_YEARS, NINETEEN_YEARS, ONE_TO_SIX_YEARS, SEVEN_TO_EIGHTEEN_YEARS, THIRTEEN_YEARS, THREE_AND_SEVEN_YEARS, TWENTYTHREE_YEARS } from '../../../utils/EpiReportTypeConstants';
import { ITreatmentReportEpiReport } from '../../../models/ITreatmentReport';
import { TreatmentReportApi } from '../../../api/TreatmentReportApi';
import { useAuth } from '../../../App';
import { IListItem } from 'soprasteria-ui-library/dist/models/IFormInput';
import { CarePerformerApi } from '../../../api/CarePerformerApi';
import { setMessage } from '../../../helpers/MessageHelper';
import { NoCarePerformersFoundMessage } from '../../../utils/CarePerformerConstants';

interface IEpiReportModal {
    epiReport?: IEpiReport;
    epiReportType: number;
    patientId: number;
    onSubmit: (epiReport: ITreatmentReportEpiReport, onCallEnd?: () => void) => void;
    closeModal: () => any;
}

export const EpiReportModal: React.FunctionComponent<IEpiReportModal> = ({ epiReport, epiReportType, patientId, onSubmit, closeModal }) => {
    const auth = useAuth();
    const api = new TreatmentReportApi(auth);
    const carePerformerApi = new CarePerformerApi(auth);
    const [isFormPending, setIsFormPending] = React.useState<boolean>(false);
    const [isReadOnly, setIsReadOnly] = React.useState<boolean>(false);
    const [epiReportTypeId, setEpiReportTypeId] = React.useState<number>(0);
    const [assessmentDate, setAssessmentDate] = React.useState<Date>(new Date());
    const [carePerformers, setCarePerformers] = React.useState<IListItem[]>([]);
    const [noCarePerformersMessage, setNoCarePerformersMessage] = React.useState<string>();
    const [isLoadingCarePerformers, setIsLoadingCarePerformers] = React.useState<boolean>(false);
    const dateToday = new Date();

    React.useEffect(() => {
        if (epiReport) {
            setEpiReportTypeId(epiReport.epiReportTypeId);
            setIsReadOnly(!epiReport?.isEditable);
        } else {
            setEpiReportTypeId(epiReportType);
            setIsReadOnly(false);
        }
        document.getElementById("open-create-or-edit-epi-report-modal")?.click();
    }, [epiReport])

    React.useEffect(() => {
        if (assessmentDate) {
            api.getEpiReportTypeId(patientId, formatDate(assessmentDate), (data: number) => {
                setEpiReportTypeId(data);
            });

            loadCarePerformers();
        }
    }, [assessmentDate])

    const loadCarePerformers = () => {
        setIsLoadingCarePerformers(true);
        carePerformerApi.getCarePerformersWithDate(formatDate(assessmentDate), (carePerformersData) => {
            let mappedCarePerformers = carePerformersData.map<IListItem>((carePerformer) => { return { text: carePerformer.carePerformerName, value: carePerformer.carePerformerId.toString() } });
            setCarePerformers(mappedCarePerformers);
            if (mappedCarePerformers.length === 0) {
                const newMessage = setMessage(NoCarePerformersFoundMessage, noCarePerformersMessage ?? "");
                setNoCarePerformersMessage(newMessage);
            } else {
                setNoCarePerformersMessage("");
            }
        }, undefined, () => setIsLoadingCarePerformers(false));
    }

    const onFormSubmit = (data: any) => {
        setIsFormPending(true);
        onSubmit(data, () => {
            setIsFormPending(false);
        });
    }

    const onAssessmentDateChange = (newAssessmentDate: Date | null) => {
        if (newAssessmentDate === null) {
            setAssessmentDate(new Date());
        } else {
            setAssessmentDate(newAssessmentDate);
        }
    }

    const renderChildTeethFields = () => {
        return (<>
            <NumberInput label="dt" name="dtChild" defaultValue={epiReport?.dtChild?.toString()} readonly={isReadOnly} placeholder="Antal mjölktänder med dentinkaries" required inlineLabel minValue={0} maxValue={20} />
            <NumberInput label="ft" name="ftChild" defaultValue={epiReport?.ftChild?.toString()} readonly={isReadOnly} placeholder="Antal mjölktänder med fyllning (ej registrerade som karies)" required inlineLabel minValue={0} maxValue={20} />
            <NumberInput label="mt/et" name="mtetChild" defaultValue={epiReport?.mtetChild?.toString()} readonly={isReadOnly} placeholder="Antal mjölktänder som extraherats pga karies" required inlineLabel minValue={0} maxValue={20} />
        </>);
    }

    const renderPermanentTeethFields = () => {
        return (<>
            <NumberInput label="DT" name="dtAdult" defaultValue={epiReport?.dtAdult?.toString()} readonly={isReadOnly} placeholder="Antal tänder med dentinkaries" required inlineLabel minValue={0} maxValue={28} />
            <NumberInput label="FT" name="ftAdult" defaultValue={epiReport?.ftAdult?.toString()} readonly={isReadOnly} placeholder="Antal tänder med fyllning (ej registrerade som karies)" required inlineLabel minValue={0} maxValue={28} />
        </>);
    }

    const renderPermanentTeethAndExtraFields = () => {
        return (<>
            <NumberInput label="DT" name="dtAdult" defaultValue={epiReport?.dtAdult?.toString()} readonly={isReadOnly} placeholder="Antal tänder med dentinkaries" required inlineLabel minValue={0} maxValue={28} />
            <NumberInput label="FT" name="ftAdult" defaultValue={epiReport?.ftAdult?.toString()} readonly={isReadOnly} placeholder="Antal tänder med fyllning (ej registrerade som karies)" required inlineLabel minValue={0} maxValue={28} />
            <NumberInput label="DS-a" name="dSaAdult" defaultValue={epiReport?.dSaAdult?.toString()} readonly={isReadOnly} placeholder="Antal sidoytor med dentinkaries" required inlineLabel minValue={0} maxValue={140} />
            <NumberInput label="FS-a" name="fSaAdult" defaultValue={epiReport?.fSaAdult?.toString()} readonly={isReadOnly} placeholder="Antal sidoytor med fyllning (ej registrerade som karierade)" required inlineLabel minValue={0} maxValue={140} />
        </>)
    }

    const renderEpiFields = (epiReportTypeId: number) => {
        switch (epiReportTypeId) {
            case THREE_AND_SEVEN_YEARS:
            case ONE_TO_SIX_YEARS:
                return renderChildTeethFields();
            case THIRTEEN_YEARS:
            case SEVEN_TO_EIGHTEEN_YEARS:
                return renderPermanentTeethFields();
            case NINETEEN_YEARS:
            case TWENTYTHREE_YEARS:
            case NINETEEN_TO_TWENTYTHREE_YEARS:
                return renderPermanentTeethAndExtraFields();
            default:
                return null;
        }
    }

    const d = epiReport ? new Date(dateTimeToDateString(epiReport.assessmentDate)) : new Date();

    return (
        <Modal id="epi-report-modal" modalSize={ModalSize.large} header={epiReport ? "Redigera EPI-rapport" : "Skapa EPI-rapport"}>
            <Form onSubmit={onFormSubmit}>
                <ModalBody>
                    <AlertMessage alertType={AlertType.danger} duration={10000}>{noCarePerformersMessage}</AlertMessage>
                    <div className="row">
                        <div className="col-12">
                            <HiddenInput name="epiReportId" value={epiReport ? epiReport.epiReportId.toString() : "-1"} />
                            <HiddenInput name="epiReportTypeId" value={epiReportTypeId.toString()} />
                            <CustomDatePicker label="Behandlingsdatum"
                                name="assessmentDate"
                                onChange={onAssessmentDateChange}
                                value={assessmentDate}
                                min={addToMonth(assessmentDate, -3)}
                                max={dateToday}
                                tooltipDescription="Avser datumet när behandlingsåtgärden utfördes på patienten, ska överensstämma med journalen"
                                disabled={isReadOnly || epiReport !== undefined}
                                required
                                inlineLabel />
                            <Select name="carePerformer.carePerformerId"
                                label="Behandlare"
                                placeholder="Välj en behandlare"
                                options={carePerformers}
                                isLoading={isLoadingCarePerformers}
                                required
                                inlineLabel />
                            {
                                renderEpiFields(epiReportTypeId)
                            }
                        </div>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <div className="text-right btn-group" role="group">
                        <CustomButton buttonType={ButtonType.general} id="close-epi-report-modal" data-dismiss="modal" onClick={() => closeModal()} buttonText="Avbryt" />
                        <CustomSubmitButton isLoading={isFormPending} disabled={isReadOnly || epiReportTypeId === 0}>Spara</CustomSubmitButton>
                        <input type="hidden" data-toggle="modal" id="open-create-or-edit-epi-report-modal" data-target="#epi-report-modal" />
                    </div>
                </ModalFooter>
            </Form>
        </Modal>
    )
}